"use client";

import { useEffect, useRef } from "react";
import { Languages } from "lucide-react";

declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

const GoogleTranslate = () => {
  const hasInitialized = useRef(false);

  useEffect(() => {
    // 1. The Initialization Logic
    const initGoogleTranslate = () => {
      if (hasInitialized.current) return;
      
      const targetDiv = document.getElementById("google_translate_element");
      
      // Ensure both the library AND the target div exist before running
      if (
        window.google &&
        window.google.translate &&
        window.google.translate.TranslateElement &&
        targetDiv
      ) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            // Consolidated Language List
            includedLanguages: "en,hi,bn,te,mr,ta,ur,gu,kn,ml,pa,or,as",
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          "google_translate_element"
        );
        hasInitialized.current = true; // Prevent double init
      }
    };

    // 2. Continuous Check (The Fix for Production Race Conditions)
    // Checks every 100ms until Google Translate is ready
    const checkInterval = setInterval(() => {
      if (window.google && window.google.translate && document.getElementById("google_translate_element")) {
        initGoogleTranslate();
        clearInterval(checkInterval); // Stop checking once done
      }
    }, 100);

    // 3. Load the Script (if not already loaded)
    const scriptId = "google-translate-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
      
      // Define global callback as a backup
      window.googleTranslateElementInit = initGoogleTranslate;
    }

    return () => clearInterval(checkInterval);
  }, []);

  return (
    <>
      {/* 4. CSS Hiding (Prevents "Top Bar" flashing) */}
      <style>
        {`
          .goog-te-banner-frame.skiptranslate { display: none !important; } 
          body { top: 0px !important; } 
          .goog-logo-link { display: none !important; } 
          .goog-te-gadget span { display: none !important; } 
          .goog-tooltip { display: none !important; }
          .goog-te-gadget { color: transparent !important; font-size: 0 !important; }
          
          /* This forces the Google Widget to be invisible but clickable over your icon */
          #google_translate_element {
             opacity: 0;
             position: absolute;
             top: 0;
             left: 0;
             width: 100%;
             height: 100%;
             z-index: 10;
             overflow: hidden;
          }
          
          /* Enlarge the internal Google dropdown to fill the container */
          .goog-te-combo {
             transform: scale(2); 
             transform-origin: top left;
             width: 100% !important;
             height: 100% !important;
             opacity: 0.01; 
             cursor: pointer;
          }
        `}
      </style>

      {/* 5. The UI Wrapper */}
      <div className="relative group w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 cursor-pointer hover:bg-gray-50 transition-all z-[99999] overflow-hidden">
        
        {/* The Visible Icon */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Languages className="w-5 h-5 text-primary" />
        </div>

        {/* The Invisible Google Widget Layer */}
        <div id="google_translate_element"></div>
      </div>
    </>
  );
};

export default GoogleTranslate;